<template>
    <div class="mb-6">
        <template v-if="vendorLogos?.length > 0">
            <div class="mb-4">
                <div class="mb-2 flex justify-between text-sm font-gotham">
                    <label v-if="canShowGroupLabels" class="font-bold mr-2">
                        {{$t('VENDOR_LOGOS')}}
                    </label>
                </div>

                <swatch-button-group
                    :option-name="this.placement.code"
                    :options="vendorLogos"
                    :selected-value="this.placement.selectedValue"
                    :variant="getPlacementVariant(this.placement)"
                    :hide-labels="true"
                    :allow-debug-mode="false"
                    @selection-changed="selectValue(this.placement, $event)"
                />
            </div>
        </template>
        <div v-for="(group, groupIndex) in optionGroups"
            :key="group.code"
            :class="{
                'mt-4': groupIndex > 0,
            }"
        >
            <div class="mb-2 flex justify-between text-sm font-gotham">
                <label v-if="canShowGroupLabels" class="font-bold mr-2">
                    {{customizerStore.useTranslations(`logoGroup_${group.group}`, group.label)}}
                </label>

                <!-- No selected names for logos, not user friendly -->
                <!--
                <label
                    v-if="group.containsSelected"
                    class="font-light overflow-hidden whitespace-nowrap text-ellipsis"
                >
                    {{group.placement.selectedName}}
                </label>
                -->
            </div>

            <swatch-button-group
                :option-name="group.placement.code"
                :options="group.components"
                :selected-value="group.placement.selectedValue"
                :variant="getPlacementVariant(group.placement)"
                :hide-labels="true"
                :allow-debug-mode="false"
                @selection-changed="selectValue(group.placement, $event)"
            />
        </div>
    </div>
</template>

<script>
    import SwatchButtonGroup from './SwatchButtonGroup.vue';

    import allStores from '../stores/all';

    export default {
        mixins: [allStores],

        components: {
            SwatchButtonGroup,
        },

        props: {
            placement: {
                type: Object,
                required: true,
            },
            mirror: {
                type: Boolean,
                default: false,
            },
        },

        computed: {
            viewController() {
                return this.customizerStore.viewController;
            },

            hiddenLogos() {
                return this.customizerStore.hiddenLogos;
            },

            canShowGroupLabels() {
                return !(this.customizerStore.options?.package?.length > 0);
            },

            vendor() {
                return this.customizerStore.vendor;
            },

            vendorLogos() {
                const list = [];

                if (this.vendor?.logoGroup?.logos) {
                    // eslint-disable-next-line no-restricted-syntax
                    for (const logo of this.vendor.logoGroup.logos) {
                        const image = `${this.viewController.blueprint.imageRootUrl}VENDORLOGO-${logo.ecomValue}`;

                        // Fake options.
                        const option = {
                            vendorLogo: true,
                            ecomValue: logo.ecomValue,

                            code: `VENDORLOGO-${logo.ecomValue}`,
                            name: logo.name,
                            custom: {
                                'fj-parameter-value': logo.ecomValue,
                                image,
                            },
                        };

                        list.push(option);
                    }
                }

                return list;
            },

            /**
             * Group options according to WebTop style option groups.
             */
            optionGroups() {
                const groups = {};

                const { placement } = this;

                const allowedLogos = this.customizerStore.allowedLogos;

                placement.components.forEach((component) => {
                    if (component.placeholder) {
                        return;
                    }

                    const ecomValue = component.custom['fj-parameter-value'];
                    if (!allowedLogos.has(ecomValue)) {
                        return;
                    }

                    if (this.hiddenLogos?.includes(ecomValue)) {
                        return;
                    }

                    const groupType = component.custom?.['fj-logo-group'];

                    let group = groups[groupType];

                    if (group == null) {
                        group = {
                            code: `${placement.code}-${groupType}`,
                            placement,

                            groupType,
                            group: component.custom?.['fj-logo-group'],
                            label: component.custom?.['fj-logo-group-label'],
                            components: [],
                        };

                        groups[groupType] = group;
                    }

                    group.components.push(component);
                });

                return Object.values(groups)
                    .map((group) => {
                        group.containsSelected = group.components.some((component) => component.code === group.placement.selectedValue);

                        return group;
                    })
                    .filter((group) => group.components.length > 0);
            },
        },

        methods: {
            getPlacementVariant() {
                return 'small';
            },

            sniffImageSize(url) {
                return new Promise((resolve, reject) => {
                    const img = new Image();

                    img.onload = () => {
                        resolve({
                            width: img.naturalWidth,
                            height: img.naturalHeight,
                        });
                    };

                    img.onerror = () => {
                        reject(new Error(`Failed to load image: ${url}`));
                    };

                    img.src = url;
                });
            },

            async selectDynamicVendorLogo(placement, option, url, size) {
                this.viewController.updateDynamicImage(placement.code, {
                    title: option.name,
                    name: `VENDORLOGO-${option.ecomValue}`,
                    url,
                    width: size.width,
                    height: size.height,
                });

                // Reset scaling and store parameters..
                this.viewController.commitCustomAttributes(placement.code, {
                    'fj-parameter-value': option.ecomValue,
                    'fj-parameter-value-name': option.name,
                    'dynamic-image-scale': '1',
                });
            },

            async selectVendorLogo(placement, option) {
                const url = `${this.viewController.blueprint.imageRootUrl}VENDORLOGO-${option.ecomValue}`;

                const size = await this.sniffImageSize(url);

                let thisSide = null;
                if (placement.code === 'MONOGRAMMINGLOGO11') {
                    // Clear standard logos.
                    this.customizerStore.clearPlacement(this.viewController, 'MONOGRAMMINGLOGO11');

                    thisSide = this.viewController.findPlacement('MONOGRAMMINGIMAGE11');
                } else {
                    // Clear standard logos.
                    this.customizerStore.clearPlacement(this.viewController, 'MONOGRAMMINGLOGO21');

                    thisSide = this.viewController.findPlacement('MONOGRAMMINGIMAGE21');
                }

                if (thisSide) {
                    await this.selectDynamicVendorLogo(thisSide, option, url, size);
                }

                if (this.mirror) {
                    let otherSide = null;
                    if (placement.code === 'MONOGRAMMINGLOGO11') {
                        // Clear standard logos.
                        this.customizerStore.clearPlacement(this.viewController, 'MONOGRAMMINGLOGO21');

                        otherSide = this.viewController.findPlacement('MONOGRAMMINGIMAGE21');
                    } else {
                        // Clear standard logos.
                        this.customizerStore.clearPlacement(this.viewController, 'MONOGRAMMINGLOGO11');

                        otherSide = this.viewController.findPlacement('MONOGRAMMINGIMAGE11');
                    }

                    if (otherSide) {
                        await this.selectDynamicVendorLogo(otherSide, option, url, size);
                    }
                }
            },

            selectValue(placement, option) {
                if (option.option?.raw?.vendorLogo) {
                    this.selectVendorLogo(placement, option.option.raw);
                    return;
                }

                if (placement.code === 'MONOGRAMMINGLOGO11') {
                    // Clear vendor logos.
                    this.customizerStore.clearPlacement(this.viewController, 'MONOGRAMMINGIMAGE11');
                } else {
                    // Clear vendor logos.
                    this.customizerStore.clearPlacement(this.viewController, 'MONOGRAMMINGIMAGE21');
                }

                this.customizerStore.setSelection(placement.code, option.value, false);

                if (this.mirror) {
                    // Find a similar placement on the other side.
                    let otherSide;

                    if (placement.code === 'MONOGRAMMINGLOGO11') {
                        otherSide = this.viewController.findPlacement('MONOGRAMMINGLOGO21');

                        // Clear vendor logos.
                        this.customizerStore.clearPlacement(this.viewController, 'MONOGRAMMINGIMAGE21');
                    } else {
                        otherSide = this.viewController.findPlacement('MONOGRAMMINGLOGO11');

                        // Clear vendor logos.
                        this.customizerStore.clearPlacement(this.viewController, 'MONOGRAMMINGIMAGE11');
                    }

                    const otherComponent = otherSide.components.find((x) => x.placementIndependentCode === option.placementIndependentCode);

                    if (otherComponent) {
                        // No undo for this.
                        this.viewController.updateComponent(otherSide.code, otherComponent.code);
                    }
                }
            },
        },
    };
</script>
